import React, { ReactElement } from "react";
import { Viewer, Worker, SpecialZoomLevel } from "@react-pdf-viewer/core";
import {
  defaultLayoutPlugin,
  ToolbarProps,
  ToolbarSlot,
  DefaultLayoutPlugin,
} from "@react-pdf-viewer/default-layout";
import { zoomPlugin, RenderZoomProps } from "@react-pdf-viewer/zoom";
import {
  Download,
  ZoomIn,
  ZoomOut,
  ArrowLeft,
  ArrowRight,
  Maximize,
} from "lucide-react";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import { ViewerWrapperProps } from "../Detail/RendererUploadView/ViewerWrapper";
import { CustomTooltip } from "../../GrammarCheckerEditor/TopMenu/CustomTooltip";
import { MdKeyboardBackspace } from "react-icons/md";
import { FaRegImage } from "react-icons/fa6";
import BoxLoading from "../Loader/BoxLoader";

const CustomToolbarExample = ({
  selectedDocument,
  onBack,
  fileName,
}: ViewerWrapperProps) => {
  const zoomPluginInstance = zoomPlugin();
  const { Zoom } = zoomPluginInstance;

  const renderToolbar = (Toolbar: (props: ToolbarProps) => ReactElement) => (
    <Toolbar>
      {(slots: ToolbarSlot) => {
        const {
          CurrentPageInput,
          CurrentScale,
          GoToNextPage,
          GoToPreviousPage,
          NumberOfPages,
          ZoomIn: ZoomInSlot,
          ZoomOut: ZoomOutSlot,
        } = slots;

        return (
          <div className="flex w-full flex-nowrap items-center justify-between gap-2 rounded-t-lg border-b border-b-[#e1e1e1] bg-[#ffffff] p-2">
            <div className="flex flex-nowrap gap-2 overflow-hidden text-ellipsis whitespace-nowrap">
              <CustomTooltip tooltip="Back" position="top">
                <button
                  className="rounded-lg p-2 transition-colors hover:border-[#e1e1e1] hover:bg-[#f5f5f5]"
                  type="button"
                  onClick={onBack}
                >
                  <MdKeyboardBackspace className="h-5 w-5 text-gray-500" />
                </button>
              </CustomTooltip>
              <div className="self-center overflow-hidden text-ellipsis whitespace-nowrap text-lg font-[500] text-gray-800">
                {fileName || "Document Viewer"}
              </div>
            </div>
            <div className="flex shrink-0">
              <div className="flex items-center gap-2">
                <CustomTooltip tooltip="Zoom Out">
                  <ZoomOutSlot>
                    {(props) => (
                      <button
                        onClick={props.onClick}
                        className="rounded-lg p-2 transition-colors hover:bg-gray-200"
                        type="button"
                      >
                        <ZoomOut className="h-5 w-5 text-gray-500" />
                      </button>
                    )}
                  </ZoomOutSlot>
                </CustomTooltip>

                <div className="min-w-[30px] text-center text-gray-500">
                  <CurrentScale>
                    {(props) => (
                      <span>{`${Math.round(props.scale * 100)}%`}</span>
                    )}
                  </CurrentScale>
                </div>

                <CustomTooltip tooltip="Zoom In">
                  <ZoomInSlot>
                    {(props) => (
                      <button
                        onClick={props.onClick}
                        className="rounded-lg p-2 transition-colors hover:bg-gray-200"
                        type="button"
                      >
                        <ZoomIn className="h-5 w-5 text-gray-500" />
                      </button>
                    )}
                  </ZoomInSlot>
                </CustomTooltip>

                <CustomTooltip tooltip="Fit to Screen">
                  <Zoom>
                    {(props: RenderZoomProps) => (
                      <button
                        onClick={() => props.onZoom(SpecialZoomLevel.PageFit)}
                        className="rounded-lg p-2 transition-colors hover:bg-gray-200"
                        type="button"
                      >
                        <Maximize className="h-5 w-5 text-gray-500" />
                      </button>
                    )}
                  </Zoom>
                </CustomTooltip>
              </div>

              <div className="flex items-center gap-2">
                <CustomTooltip tooltip="Previous Page">
                  <GoToPreviousPage>
                    {(props) => (
                      <button
                        disabled={props.isDisabled}
                        onClick={props.onClick}
                        className={`rounded-lg p-2 transition-colors hover:bg-gray-200 ${
                          props.isDisabled
                            ? "pointer-events-none opacity-50"
                            : ""
                        }`}
                        type="button"
                      >
                        <ArrowLeft className="h-5 w-5 text-gray-500" />
                      </button>
                    )}
                  </GoToPreviousPage>
                </CustomTooltip>

                <div className="flex items-center text-gray-500">
                  <div className="w-8">
                    <CurrentPageInput />
                  </div>
                  <span className="mx-2 ml-3">/</span>
                  <NumberOfPages />
                </div>

                <CustomTooltip tooltip="Next Page">
                  <GoToNextPage>
                    {(props) => (
                      <button
                        disabled={props.isDisabled}
                        onClick={props.onClick}
                        className={`rounded-lg p-2 transition-colors hover:bg-gray-200 ${
                          props.isDisabled
                            ? "cursor-not-allowed opacity-50"
                            : ""
                        }`}
                        type="button"
                      >
                        <ArrowRight className="h-5 w-5 text-gray-700" />
                      </button>
                    )}
                  </GoToNextPage>
                </CustomTooltip>
              </div>
            </div>
          </div>
        );
      }}
    </Toolbar>
  );

  const defaultLayoutPluginInstance: DefaultLayoutPlugin = defaultLayoutPlugin({
    renderToolbar,
    sidebarTabs: (defaultTabs) => [
      {
        content: defaultTabs[0].content,
        icon: (
          <CustomTooltip tooltip="Thumbnail Images" position="bottom">
            <button
              className="mt-1 rounded-lg p-2 transition-colors hover:bg-gray-200"
              type="button"
            >
              <FaRegImage className="h-5 w-5 text-gray-500" />
            </button>
          </CustomTooltip>
        ),
        title: "Thumbnail Images",
      },
    ],
  });

  return (
    <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js">
      {selectedDocument ? (
        <Viewer
          fileUrl={selectedDocument}
          plugins={[defaultLayoutPluginInstance, zoomPluginInstance]}
          defaultScale={SpecialZoomLevel.PageFit}
          theme={"light"}
          renderLoader={() => <BoxLoading text="Loading document ..." />}
        />
      ) : (
        <BoxLoading text="No document selected." />
      )}
    </Worker>
  );
};

export default CustomToolbarExample;
